/**
 * EventTypeModal Component
 *
 * Modal for creating and editing event types with their template roles.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  EventType,
  createEventType,
  updateEventType,
} from '../../api/eventTypes';
import { getErrorMessage } from '../../api/client';
import EventTypeRoleEditor, {
  RoleInput,
  apiRolesToEditorRoles,
  apiRolesToNewEditorRoles,
  editorRolesToApiRoles,
} from './EventTypeRoleEditor';

/**
 * First free "<name> (Copy)" / "<name> (Copy 2)" … for a duplicate.
 *
 * Names are unique per org case-insensitively (enforced by the API), so the
 * suggestion has to dodge every existing one — otherwise every duplicate
 * opens pre-broken. Kept under the API's 100-char cap by trimming the base,
 * never the suffix, since the suffix is what makes it unique.
 */
export function suggestCopyName(baseName: string, existingNames: string[]): string {
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  const MAX = 100;

  for (let n = 1; ; n++) {
    const suffix = n === 1 ? ' (Copy)' : ` (Copy ${n})`;
    const base = baseName.trim().slice(0, MAX - suffix.length).trim();
    const candidate = `${base}${suffix}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

interface EventTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  eventType?: EventType | null;
  /** Source template to copy. Opens create mode prefilled from it. */
  duplicateFrom?: EventType | null;
  /** Every event type name in the org — used for the inline collision check. */
  existingNames?: string[];
}

export default function EventTypeModal({
  isOpen,
  onClose,
  onSuccess,
  eventType,
  duplicateFrom,
  existingNames = [],
}: EventTypeModalProps) {
  const isEditing = !!eventType;
  const isDuplicating = !isEditing && !!duplicateFrom;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [roles, setRoles] = useState<RoleInput[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or its subject changes
  useEffect(() => {
    if (isOpen) {
      if (eventType) {
        setName(eventType.name);
        setDescription(eventType.description || '');
        setRoles(apiRolesToEditorRoles(eventType.templateRoles));
      } else if (duplicateFrom) {
        // Roles come across as new rows; the name is pre-deduped so the copy
        // is savable on sight, and stays editable.
        setName(suggestCopyName(duplicateFrom.name, existingNames));
        setDescription(duplicateFrom.description || '');
        setRoles(apiRolesToNewEditorRoles(duplicateFrom.templateRoles));
      } else {
        setName('');
        setDescription('');
        setRoles([]);
      }
      setError(null);
    }
    // existingNames is only read to seed the initial suggestion; re-running on
    // every list refetch would clobber whatever the user has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventType, duplicateFrom]);

  /**
   * Names already in use, case-insensitively — an event type's own name is
   * excluded so re-saving it unchanged isn't a "collision" with itself.
   */
  const takenNames = useMemo(() => {
    const own = eventType?.name.trim().toLowerCase();
    return new Set(
      existingNames
        .map((n) => n.trim().toLowerCase())
        .filter((n) => n.length > 0 && n !== own)
    );
  }, [existingNames, eventType]);

  const trimmedName = name.trim();
  const nameTaken = trimmedName.length > 0 && takenNames.has(trimmedName.toLowerCase());

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Event type name is required');
      return;
    }

    // The API is the real authority; catching it here keeps a duplicate from
    // round-tripping to a 409. Both sides check ACTIVE types only, so a
    // soft-deleted type's name can be reused.
    if (nameTaken) {
      setError('An event type with this name already exists');
      return;
    }

    // Validate roles - each must have a name
    const invalidRole = roles.find((r) => !r.roleName.trim());
    if (invalidRole) {
      setError('All roles must have a name');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditing && eventType) {
        // Update existing event type
        await updateEventType(eventType.id, {
          name: name.trim(),
          description: description.trim() || null,
        });
        // Note: Role updates would need separate API calls in a real implementation
        // For simplicity, we're only updating name/description here
      } else {
        // Create new event type with roles
        await createEventType({
          name: name.trim(),
          description: description.trim() || null,
          roles: editorRolesToApiRoles(roles),
        });
      }
      onSuccess();
      handleClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setRoles([]);
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] flex flex-col border border-hairline">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 h-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing
                ? 'Edit Event Type'
                : isDuplicating
                ? 'Duplicate Event Type'
                : 'Create Event Type'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {isEditing
                ? 'Update the event type details'
                : isDuplicating
                ? `Copied from "${duplicateFrom!.name}". Give it a new name, then adjust the roles.`
                : 'Create a template with predefined roles for quick event setup'}
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={nameTaken}
                aria-describedby={nameTaken ? 'name-taken' : undefined}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                  nameTaken
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-rack focus:border-rack'
                }`}
                placeholder="e.g., Men's Basketball, Concert, Corporate Event"
              />
              {nameTaken && (
                <p id="name-taken" className="mt-1 text-sm text-red-600">
                  "{trimmedName}" is already an event type in this organization. Pick a
                  different name.
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                placeholder="Brief description of this event type..."
              />
            </div>

            {/* Roles Editor */}
            {!isEditing && (
              <EventTypeRoleEditor
                roles={roles}
                onChange={setRoles}
                disabled={isSubmitting}
              />
            )}

            {isEditing && (
              <div className="p-3 bg-surface-panel border border-hairline border-l-[3px] border-l-rack">
                <p className="text-sm text-blue-700">
                  To manage template roles, use the role management options on the event type card.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !trimmedName || nameTaken}
              title={nameTaken ? 'That name is already taken in this organization' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? 'Saving...'
                : isEditing
                ? 'Save Changes'
                : isDuplicating
                ? 'Create Copy'
                : 'Create Event Type'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
